import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import { createElement } from "react";
import { traceHeaders } from "@iamu/telemetry/browser";
import { identifyUser, resetUser } from "./posthog";

export interface AuthInfo {
  authMode: "none" | "api-key" | "oidc" | "dev";
  tenantId?: string;
  userId?: string;
  email?: string;
  displayName?: string | null;
  avatarUrl?: string | null;
  defaultProjectId?: string | null;
}

export type AuthErrorCode = "unauthenticated" | "service_error";

export class AuthError extends Error {
  constructor(
    message: string,
    public readonly code: AuthErrorCode,
  ) {
    super(message);
    this.name = "AuthError";
  }
}

interface AuthContextValue {
  auth: AuthInfo | null;
  isLoading: boolean;
  error: string | null;
  errorCode: AuthErrorCode | null;
}

const AuthContext = createContext<AuthContextValue>({
  auth: null,
  isLoading: true,
  error: null,
  errorCode: null,
});

/**
 * Returns non-auth headers for API requests (trace headers, optional api-key).
 * Auth is handled via httpOnly cookies — no Bearer tokens needed.
 */
export function getAuthHeaders(): Record<string, string> {
  const trace = traceHeaders();

  // api-key mode: embed the key from build-time env var
  const apiKey = import.meta.env.VITE_API_KEY as string | undefined;
  if (apiKey) {
    return { "x-api-key": apiKey, ...trace };
  }

  return trace;
}

/**
 * Fetch wrapper that includes credentials (cookies) and trace headers.
 * Use this instead of raw fetch() for all API calls.
 */
export function authedFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const headers = { ...getAuthHeaders(), ...init?.headers };
  return fetch(input, {
    ...init,
    credentials: "include",
    headers,
  });
}

/** True when auth provides user identity (billing, profiles, collaboration). */
export function hasUserAuth(auth: AuthInfo | null): boolean {
  return auth?.userId != null;
}

async function initAuth(): Promise<AuthInfo> {
  // Clean up legacy localStorage keys from the old client-side OIDC + last-project
  localStorage.removeItem("iamu:lastProjectId");
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith("oidc.")) {
      localStorage.removeItem(key);
    }
  }

  let res: Response;
  try {
    res = await authedFetch("/api/auth/me");
  } catch {
    throw new AuthError("Cannot reach API", "service_error");
  }

  if (res.status === 401) {
    throw new AuthError("Session expired", "unauthenticated");
  }
  if (!res.ok) {
    throw new AuthError(`API error ${res.status}`, "service_error");
  }
  return (await res.json()) as AuthInfo;
}

// Retry delays for transient service errors (ms)
const RETRY_DELAYS = [1_000, 3_000, 8_000];

export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<AuthInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<AuthErrorCode | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      for (let attempt = 0; attempt <= RETRY_DELAYS.length; attempt++) {
        try {
          const data = await initAuth();
          if (!cancelled) {
            setAuth(data);
            setIsLoading(false);
            if (data.userId) {
              identifyUser(data.userId, {
                email: data.email,
                name: data.displayName,
                tenantId: data.tenantId,
                authMode: data.authMode,
              });
            }
          }
          return;
        } catch (err) {
          const isUnauth =
            err instanceof AuthError && err.code === "unauthenticated";

          // Don't retry unauthenticated — the session is definitely gone
          if (isUnauth || attempt === RETRY_DELAYS.length) {
            if (!cancelled) {
              setError(err instanceof Error ? err.message : String(err));
              setErrorCode(isUnauth ? "unauthenticated" : "service_error");
              setIsLoading(false);
              if (isUnauth) {
                resetUser();
              }
            }
            return;
          }

          // Service error — wait and retry (spinner stays up)
          await new Promise((resolve) =>
            setTimeout(resolve, RETRY_DELAYS[attempt]),
          );
        }
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  return createElement(
    AuthContext.Provider,
    { value: { auth, isLoading, error, errorCode } },
    children,
  );
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}

export function useAuthErrorCode(): AuthErrorCode | null {
  return useContext(AuthContext).errorCode;
}

export function useTenant(): string {
  const { auth, isLoading, error } = useAuth();
  if (isLoading) {
    throw new Error("Auth context is still loading");
  }
  if (error) {
    throw new Error(`Auth error: ${error}`);
  }
  if (!auth?.tenantId) {
    throw new Error("No tenant resolved");
  }
  return auth.tenantId;
}
